import { useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import api, { API_BASE_URL } from './api';

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Nicosia',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  } catch (_err) {
    return d.toLocaleTimeString();
  }
}

function SendIcon({ color = 'currentColor' } = {}) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3.4 20.2 21 12 3.4 3.8 3 10.3l10 1.7-10 1.7.4 6.5Z"
        fill={color}
      />
    </svg>
  );
}

function useSupportSocket(token) {
  const socketRef = useRef(null);

  useEffect(() => {
    if (!API_BASE_URL) return undefined;
    const socket = io(`${API_BASE_URL}/support`, {
      transports: ['websocket'],
      auth: { token: token || '' },
    });
    socketRef.current = socket;

    return () => {
      socketRef.current = null;
      socket.disconnect();
    };
  }, [token]);

  return socketRef;
}

export default function AdminSupportSection({ token }) {
  const socketRef = useSupportSocket(token);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const selectedConversation = useMemo(
    () => conversations.find((c) => String(c._id) === String(selectedId)) || null,
    [conversations, selectedId]
  );

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/admin/support/conversations?status=open&limit=50&page=1');
        const items = res.data?.data?.items || [];
        if (active) {
          setConversations(items);
          if (!selectedId && items.length > 0) {
            openConversation(items[0]._id);
          }
        }
      } catch (err) {
        if (active) setError(err?.response?.data?.message || 'Failed to load conversations');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return undefined;

    function joinSelected() {
      if (!selectedId) return;
      socket.emit('support:join', { conversationId: selectedId });
    }

    socket.on('connect', joinSelected);
    if (socket.connected) joinSelected();

    function onConversationUpdate(payload) {
      const conversationId = String(payload?.conversationId || '');
      if (!conversationId) return;
      setConversations((prev) => prev.map((c) => (
        String(c._id) === conversationId ? { ...c, ...payload } : c
      )));
    }

    socket.on('support:conversation-update', onConversationUpdate);
    return () => {
      socket.off('connect', joinSelected);
      socket.off('support:conversation-update', onConversationUpdate);
    };
  }, [socketRef, selectedId]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return undefined;

    function onMessage(msg) {
      const convId = String(msg?.conversationId || msg?.conversationId?._id || msg?.conversationId || '');
      const messageConvId = String(msg?.conversationId || '');
      const targetConvId = messageConvId || convId;
      if (!targetConvId) return;
      if (String(targetConvId) !== String(selectedId)) return;
      setMessages((prev) => {
        const id = String(msg?._id || '');
        if (id && prev.some((m) => String(m._id) === id)) return prev;
        return [...prev, msg];
      });
    }

    socket.on('support:message', onMessage);
    return () => socket.off('support:message', onMessage);
  }, [socketRef, selectedId]);

  async function openConversation(conversationId) {
    setSelectedId(String(conversationId));
    setLoadingMessages(true);
    setError('');
    try {
      const res = await api.get(`/admin/support/conversations/${conversationId}/messages?limit=120`);
      const nextMessages = res.data?.data?.messages || [];
      setMessages(nextMessages);

      await api.post(`/admin/support/conversations/${conversationId}/read`);
      setConversations((prev) => prev.map((c) => (
        String(c._id) === String(conversationId) ? { ...c, unreadForAdmin: 0 } : c
      )));

      const socket = socketRef.current;
      if (socket) {
        socket.emit('support:join', { conversationId });
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  }

  async function send() {
    if (!selectedId) return;
    if (!String(text || '').trim()) return;

    setSending(true);
    setError('');
    try {
      const res = await api.post(`/admin/support/conversations/${selectedId}/messages`, {
        text: String(text || '').trim(),
      });
      const msg = res.data?.data?.message;
      if (msg) {
        setMessages((prev) => (prev.some((m) => String(m._id) === String(msg._id)) ? prev : [...prev, msg]));
      }
      setText('');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  }

  const styles = {
    wrap: { display: 'grid', gridTemplateColumns: '340px minmax(0, 1fr)', gap: 14, height: 'calc(100vh - 150px)', minHeight: '70vh', overflow: 'hidden' },
    left: { borderRadius: 18, border: '1px solid rgba(166, 106, 75, 0.12)', background: 'rgba(255,251,247,0.9)', overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 },
    right: { borderRadius: 18, border: '1px solid rgba(166, 106, 75, 0.12)', background: 'rgba(255,251,247,0.9)', overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 },
    leftHeader: { padding: 14, fontWeight: 900, color: '#2b1a13', borderBottom: '1px solid rgba(166,106,75,0.10)' },
    list: { flex: 1, minHeight: 0, overflowY: 'auto' },
    row: (active) => ({
      padding: 12,
      cursor: 'pointer',
      display: 'grid',
      gap: 6,
      borderBottom: '1px solid rgba(166,106,75,0.08)',
      background: active ? 'rgba(214,132,93,0.16)' : 'transparent',
    }),
    rowTop: { display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' },
    name: { fontWeight: 900, color: '#2b1a13', fontSize: 13 },
    meta: { color: '#7b665c', fontSize: 11, whiteSpace: 'nowrap' },
    preview: { color: '#5d4a43', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    badge: { background: '#b44f28', color: '#fff', borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 900 },
    chatHeader: { padding: 14, borderBottom: '1px solid rgba(166,106,75,0.10)', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' },
    chatBody: {
      padding: 14,
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      overscrollBehavior: 'contain',
      touchAction: 'pan-y',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      flex: 1,
      minHeight: 0,
    },
    bubble: (mine) => ({
      alignSelf: mine ? 'flex-end' : 'flex-start',
      maxWidth: '78%',
      borderRadius: 16,
      padding: '10px 12px',
      background: mine ? 'linear-gradient(145deg, rgba(214,132,93,0.96), rgba(182,103,67,0.96))' : 'rgba(248,242,237,0.94)',
      border: mine ? '1px solid rgba(184,105,67,0.34)' : '1px solid rgba(166, 106, 75, 0.10)',
      color: mine ? '#fffaf7' : '#2b1a13',
      display: 'inline-block',
      width: 'fit-content',
    }),
    msgRow: { display: 'flex', alignItems: 'flex-end', gap: 10 },
    msgBody: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', flex: 1, minWidth: 0 },
    timeText: (mine) => ({ fontSize: 11, opacity: mine ? 0.85 : 0.7, whiteSpace: 'nowrap' }),
    composer: { padding: 14, borderTop: '1px solid rgba(166,106,75,0.10)', display: 'grid', gap: 10 },
    inputWrap: { position: 'relative' },
    input: { width: '100%', borderRadius: 14, border: '1px solid rgba(166, 106, 75, 0.16)', padding: '8px 56px 8px 10px', outline: 'none', resize: 'none', minHeight: 46, fontSize: 15, lineHeight: 1.3 },
    sendIconBtn: (disabled) => ({
      position: 'absolute',
      right: 8,
      top: 8,
      bottom: 8,
      width: 40,
      borderRadius: 14,
      border: '1px solid rgba(166, 106, 75, 0.18)',
      background: disabled ? 'rgba(247, 241, 235, 0.6)' : 'linear-gradient(145deg, rgba(214,132,93,0.96), rgba(182,103,67,0.96))',
      color: disabled ? '#7b665c' : '#fffaf7',
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'grid',
      placeItems: 'center',
    }),
    btnRow: { display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
    btn: (disabled) => ({
      borderRadius: 14,
      border: '1px solid rgba(166, 106, 75, 0.18)',
      background: disabled ? 'rgba(247, 241, 235, 0.6)' : 'linear-gradient(145deg, rgba(214,132,93,0.96), rgba(182,103,67,0.96))',
      color: disabled ? '#7b665c' : '#fffaf7',
      padding: '10px 14px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontWeight: 900,
    }),
    fileNote: { color: '#7b665c', fontSize: 12, fontWeight: 800 },
    error: { color: '#9c2d2d', fontWeight: 900, fontSize: 12 },
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.left}>
        <div style={styles.leftHeader}>Helpdesk Inbox</div>
        {error && <div style={{ padding: 12, ...styles.error }}>{error}</div>}
        {loading && <div style={{ padding: 12 }}>Loading…</div>}
        {!loading && (
          <div style={styles.list}>
            {conversations.length === 0 && <div style={{ padding: 12, color: '#7b665c' }}>No conversations yet.</div>}
            {conversations.map((c) => {
              const isActive = String(c._id) === String(selectedId);
              const title = c.userId?.name
                || c.userId?.username
                || c.externalPlayerId?.name
                || (c.principalType === 'guest' ? `Guest (${String(c.guestIpHash || '').slice(0, 8)})` : 'User');
              return (
                <div key={c._id} style={styles.row(isActive)} onClick={() => openConversation(c._id)}>
                  <div style={styles.rowTop}>
                    <div style={styles.name}>{title}</div>
                    <div style={styles.meta}>{formatTime(c.lastMessageAt || c.updatedAt)}</div>
                  </div>
                  <div style={styles.preview}>{c.lastMessagePreview || ''}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div style={styles.meta}>{String(c.status || '').toUpperCase()}</div>
                    {Number(c.unreadForAdmin || 0) > 0 && <div style={styles.badge}>{c.unreadForAdmin}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={styles.right}>
        <div style={styles.chatHeader}>
          <div style={{ fontWeight: 900, color: '#2b1a13' }}>
            {selectedConversation
              ? (selectedConversation.userId?.name
                || selectedConversation.userId?.username
                || selectedConversation.externalPlayerId?.name
                || (selectedConversation.principalType === 'guest' ? `Guest (${String(selectedConversation.guestIpHash || '').slice(0, 8)})` : 'Conversation'))
              : 'Select a conversation'}
          </div>
          {selectedConversation && (
            <button
              type="button"
              onClick={async () => {
                try {
                  await api.patch(`/admin/support/conversations/${selectedConversation._id}`, {
                    status: selectedConversation.status === 'open' ? 'closed' : 'open',
                  });
                  setConversations((prev) => prev.map((c) => (
                    String(c._id) === String(selectedConversation._id)
                      ? { ...c, status: c.status === 'open' ? 'closed' : 'open' }
                      : c
                  )));
                } catch (_err) {
                  setError('Failed to update status');
                }
              }}
              style={{ borderRadius: 14, border: '1px solid rgba(166,106,75,0.16)', background: '#fff8f3', padding: '10px 12px', cursor: 'pointer', fontWeight: 900 }}
            >
              {selectedConversation.status === 'open' ? 'Close' : 'Reopen'}
            </button>
          )}
        </div>

        <div className="support-scroll" style={styles.chatBody}>
          {loadingMessages && <div style={{ color: '#7b665c' }}>Loading messages…</div>}
          {!loadingMessages && selectedConversation && messages.length === 0 && (
            <div style={{ color: '#7b665c' }}>No messages yet.</div>
          )}
          {!loadingMessages && selectedConversation && messages.map((m) => {
            const mine = m.senderType === 'admin';
            return (
              <div key={m._id || `${m.createdAt}-${Math.random()}`} style={styles.bubble(mine)}>
                {m.text && (
                  <div style={styles.msgRow}>
                    <div style={styles.msgBody}>{m.text}</div>
                    <div style={styles.timeText(mine)}>{formatTime(m.createdAt)}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={styles.composer}>
          {selectedConversation ? (
            <>
              <div style={styles.inputWrap}>
                <textarea
                  rows={3}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Type a reply…"
                  style={styles.input}
                />
                <button type="button" onClick={send} disabled={sending} style={styles.sendIconBtn(sending)}>
                  <SendIcon />
                </button>
              </div>
              <div style={styles.btnRow}>
                <div />
                <div />
              </div>
            </>
          ) : (
            <div style={{ color: '#7b665c' }}>Choose a conversation from the left.</div>
          )}
        </div>
      </div>
    </div>
  );
}
